import React, { useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { IconButton, MenuItem, Menu, Chip } from "@mui/material";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import TopHeader from "../../common/Header/TopHeader";
import { 
  getAllLabTemplates, 
  deleteLabTemplate 
} from "../services/labTestTemplateService";

// Componente Modal
function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  footer = null,
}) {
  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className={`modal-dialog modal-${size} modal-dialog-centered`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

// Modal de confirmación
function ConfirmationModal({ isOpen, onClose, onConfirm, title, message }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm}>
            Confirm
          </button>
        </>
      }
    >
      <p>{message}</p>
    </Modal>
  );
}

const LaboratoryTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await getAllLabTemplates();
      console.log("API Response:", response);
      setTemplates(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching lab templates:", err);
      setError("Failed to load laboratory templates. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (template) => {
    navigate("/laboratoryTemplateAdd", { state: { templateData: template } });
  };

  const handleDelete = (template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      await deleteLabTemplate(selectedTemplate._id);
      fetchTemplates();
      alert("Laboratory template deleted successfully");
    } catch (err) {
      console.error("Error deleting template:", err);
      alert(`Error deleting template: ${err.message}`);
    } finally {
      setShowDeleteModal(false);
      setSelectedTemplate(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getCategoryBadgeClass = (category) => {
    const categoryColors = {
      "Hematología": "bg-danger",
      "Bioquímica": "bg-primary",
      "Uroanálisis": "bg-warning text-dark",
      "Coprología": "bg-info",
      "Endocrinología": "bg-success",
      "Inmunología": "bg-secondary",
      "Microbiología": "bg-dark"
    };
    return categoryColors[category] || "bg-light text-dark";
  };

  const getSampleTypeBadge = (sampleType) => {
    const colors = {
      "Sangre": "danger",
      "Orina": "warning",
      "Heces": "info",
      "Saliva": "secondary",
      "Tejido": "dark",
      "Otro": "light"
    };
    return colors[sampleType] || "light";
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => "",
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = useState(null);
          const open = Boolean(anchorEl);

          const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          return (
            <>
              <IconButton onClick={handleClick}>
                <span>⋮</span>
              </IconButton>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem
                  onClick={() => {
                    handleEdit(row.original);
                    handleClose();
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleDelete(row.original);
                    handleClose();
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
            </>
          );
        },
        size: 50,
      },
      {
        accessorFn: (row) => row.name || "Unnamed",
        id: "name",
        header: "Name",
        Cell: ({ row }) => (
          <div
            className="d-flex align-items-center"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleEdit(row.original)}
          >
            <span>{row.original.name || "Unnamed"}</span>
          </div>
        ),
      },
      {
        accessorFn: (row) => row.code || "N/A",
        id: "code",
        header: "Code",
      },
      {
        accessorFn: (row) => row.loincCode || "N/A",
        id: "loincCode",
        header: "LOINC Code",
      },
      {
        accessorFn: (row) => row.category || "Unknown",
        id: "category",
        header: "Category",
        Cell: ({ row }) => (
          <span
            className={`badge ${getCategoryBadgeClass(row.original.category)}`}
          >
            {row.original.category || "Unknown"}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.sampleType || "N/A",
        id: "sampleType",
        header: "Sample Type",
        Cell: ({ row }) => (
          <Chip
            label={row.original.sampleType || "N/A"}
            color={getSampleTypeBadge(row.original.sampleType)}
            size="small"
          />
        ),
      },
      {
        accessorFn: (row) => row.parameters?.length || 0,
        id: "parametersCount",
        header: "Parameters",
        Cell: ({ row }) => (
          <span className="badge bg-secondary">
            {row.original.parameters?.length || 0}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.turnaroundTime,
        id: "turnaroundTime",
        header: "Turnaround Time",
        Cell: ({ row }) => {
          const tt = row.original.turnaroundTime;
          return tt ? `${tt.value} ${tt.unit}` : "N/A";
        },
      },
      {
        accessorFn: (row) => row.price?.amount || 0,
        id: "price",
        header: "Price",
        Cell: ({ row }) => {
          const price = row.original.price;
          return price ? `${price.amount} ${price.currency}` : "N/A";
        },
      },
      {
        accessorFn: (row) => row.metadata?.requiresFasting,
        id: "fasting",
        header: "Fasting",
        Cell: ({ row }) => (
          <span className={`badge ${row.original.metadata?.requiresFasting ? 'bg-warning text-dark' : 'bg-secondary'}`}>
            {row.original.metadata?.requiresFasting ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.metadata?.priority || "routine",
        id: "priority",
        header: "Priority",
        Cell: ({ row }) => {
          const priority = row.original.metadata?.priority || "routine";
          const priorityColors = {
            routine: "bg-secondary",
            urgent: "bg-warning text-dark",
            stat: "bg-danger"
          };
          return (
            <span className={`badge ${priorityColors[priority]}`}>
              {priority.toUpperCase()}
            </span>
          );
        },
      },
      {
        accessorFn: (row) => row.createdAt,
        id: "createdAt",
        header: "Created",
        Cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        accessorFn: (row) => row.updatedAt,
        id: "updatedAt",
        header: "Last Modified",
        Cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const handleAddNew = () => {
    navigate("/laboratoryTemplateAdd");
  };

  if (error) {
    return (
      <div className="container-fluid px-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <main id="app">
      <TopHeader />
      <>
        <div className="container-fluid px-4">
          <div className="d-flex justify-content-between align-items-center my-3">
            <span className="fw-bold">Laboratory Test Templates</span>
            <Button onClick={handleAddNew}>Add New Template</Button>
          </div>

          {!isLoading && (
            <div className="mb-3">
              <small className="text-muted">
                Total templates loaded: {templates.length}
              </small>
            </div>
          )}

          <MaterialReactTable
            columns={columns}
            data={templates}
            state={{ isLoading }}
            enableRowSelection={true}
            enableColumnFilters
            enableColumnOrdering
            enableSorting
            enablePagination
            enableGlobalFilter
            positionGlobalFilter="left"
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => console.log('Row data:', row.original),
              sx: { cursor: 'pointer' }
            })}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Delete Laboratory Template"
            message={`Are you sure you want to delete the template "${selectedTemplate?.name || "Unnamed"}"? This action cannot be undone.`}
          />
        </div>
      </>
    </main>
  );
};

export default LaboratoryTemplates;